/* eslint "react/display-name": "warn" */
import React, { memo, useCallback, FC, Ref, CSSProperties } from 'react'
import Marker from './Marker'
import styled from 'styled-components'
import { Text, Box } from '@realsoftworks/decor'
import getEquityType from 'common/util/getEquityType'
import { useSyncedIsHovered } from 'search/SyncedMapListContext'
import { OwnerType, GoogleMap } from 'types'
import isNumber from 'common/util/isNumber'
import { useLogError } from 'common/util/hooks/useLogError'

type PropertyMarkerProps = {
  ownerType?: OwnerType
  loanToValue?: number
  isForeclosure?: boolean
  isVacant?: boolean
}
const PropertyMarker: FC<PropertyMarkerProps> = ({
  ownerType = 'owneroccupied',
  loanToValue,
  isForeclosure,
  isVacant
}) => (
  <Marker>
    <Text
      as='div'
      textAlign='center'
      color='red.300'
      lineHeight='36px'
      fontSize={3}
    >
      {renderEquityType(loanToValue)}
      {renderOwnerType(ownerType)}

      <AttributesSubscript isForeclosure={isForeclosure} isVacant={isVacant} />
    </Text>
  </Marker>
)

export default PropertyMarker

const AttributesSubscript: FC<{
  isForeclosure?: boolean
  isVacant?: boolean
  isHighlighted?: boolean
}> = ({
  isForeclosure = false,
  isVacant = false,
  isHighlighted = false
}) => {
  const attrCount = [isForeclosure, isVacant].filter(Boolean).length

  if (attrCount === 0) return null
  return (
    <Text
      color='red.500'
      fontSize={isHighlighted ? 4 : 2}
      fontWeight={700}
      css={{
        position: 'absolute',
        left: isHighlighted ? '8px' : '4px',
        bottom: 0
      }}
    >
      {attrCount > 1
        ? attrCount
        : isForeclosure
          ? 'F'
          : isVacant
            ? 'V'
            : null}
    </Text>
  )
}

type SyncedPropertyMarkerProps = Omit<
  MemoedSyncedPropertyMarkerProps,
  'hoverRef' | 'isHighlighted'
> & {
  map?: GoogleMap
  lat?: number
  lng?: number
  id?: string
  groupId?: string
}

export const SyncedPropertyMarker: FC<SyncedPropertyMarkerProps> = ({
  id,
  groupId,
  ownerType,
  loanToValue,
  map,
  lat,
  lng,
  ...props
}) => {
  const showMarkerOnPartnerHover = useCallback((isHovered, { isSelf }) => {
    if (!map || !isNumber(lat) || !isNumber(lng)) return
    const isPartnerHovered = !isSelf && isHovered
    const isMarkerVisible = map?.getBounds()?.contains({ lat, lng })
    if (isPartnerHovered && !isMarkerVisible) map?.setCenter({ lat, lng })
  }, [map])

  useLogError({
    error: 'id is unexpectedly falsy in SyncedPropertyMarker',
    shouldLog: !id
  }, [!!id])

  const { isSyncHovered: isHighlighted, hoverRef } = useSyncedIsHovered({
    memberId: id,
    groupId,
    onSyncHover: showMarkerOnPartnerHover
  })
  return (
    <MemoedSyncedPropertyMarker
      {...props}
      ownerType={ownerType}
      loanToValue={loanToValue}
      hoverRef={hoverRef}
      isHighlighted={isHighlighted}
    />
  )
}

type MemoedSyncedPropertyMarkerProps = PropertyMarkerProps & {
  ownerType?: OwnerType | undefined
  loanToValue?: number
  hoverRef: Ref<HTMLElement | undefined>
  isHighlighted: boolean
  style?: CSSProperties
  onClick?: () => void
}

const MemoedSyncedPropertyMarker = memo<MemoedSyncedPropertyMarkerProps>(({
  ownerType,
  loanToValue,
  hoverRef,
  isHighlighted,
  style = {},
  isForeclosure,
  isVacant,
  onClick,
  ...props
}) => (
  <Marker
    {...props}
    ref={hoverRef}
    onClick={onClick}
    style={{
      ...(isHighlighted && { zIndex: 1 }),
      ...(onClick && { cursor: 'pointer' }),
      ...style
    }}
  >
    <Box css='position: relative;'>
      {renderEquityType(loanToValue, { isHighlighted })}
      {renderOwnerType(ownerType, { isHighlighted })}

      <AttributesSubscript
        isForeclosure={isForeclosure}
        isVacant={isVacant}
        isHighlighted={isHighlighted}
      />
    </Box>
  </Marker>
))

const renderEquityType = (
  loanToValue,
  { isHighlighted } = { isHighlighted: false }
) => {
  const type = getEquityType(loanToValue)

  const Equity = type === 'OWNED'
    ? FreeAndClearEquity
    : type === 'UPSIDE_DOWN'
      ? UpsideDown
      : type === 'HIGH'
        ? HighEquity
        : type === 'LOW'
          ? LowEquity
          : null

  if (!Equity) return null

  return <Equity isHighlighted={isHighlighted} />
}

const renderOwnerType = (
  ownerType?: OwnerType,
  { isHighlighted }: { isHighlighted: boolean } = { isHighlighted: false }
) => {
  const OwnerType = (ownerType && OWNER_TYPE_TO_MARKER[ownerType]) ||
    Unknown
  return <OwnerType isHighlighted={isHighlighted} />
}

const PinpointContainer = styled(Box)`
  transform: translate(-50%, calc(-100% + 5px));
`

const HighlightContainerMarker = styled(Box)`
  ${p => p.isHighlighted ? `
    transform: scale(1.5) translateY(-6px);
    transformOrigin: 50% 100%;
  ` : ''}
`

type MarkerGraphic = FC<{ isHighlighted: boolean }>

const Home: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='16' cy='16' r='12.5' fill='#55AAD4' stroke='#55AAD4' />
        <path d='M9.77212 24.5335L22.7114 24.4598L16.1779 35.7761L9.77212 24.5335Z' fill='#55AAD4' />
        <path d='M10 27C15.2941 30.52 15.7647 36.8267 16 38' stroke='#55AAD4' />
        <path d='M22 27C17.3333 31.2308 16.2353 36.8267 16 38' stroke='#55AAD4' />
        <path d='M20.8609 17.8148V21.2981C20.8609 21.6214 20.6008 21.8815 20.2775 21.8815H17.4578C17.2973 21.8815 17.1661 21.7502 17.1661 21.5898V18.8673C17.1661 18.7069 17.0348 18.5756 16.8744 18.5756H15.1242C14.9638 18.5756 14.8325 18.7069 14.8325 18.8673V21.5898C14.8325 21.7502 14.7012 21.8815 14.5408 21.8815H11.7211C11.3978 21.8815 11.1377 21.6214 11.1377 21.2981V17.8148C11.1377 17.7273 11.1766 17.6446 11.2446 17.5887L15.8145 13.8258C15.9215 13.7383 16.0771 13.7383 16.1864 13.8258L20.7564 17.5887C20.822 17.6446 20.8609 17.7273 20.8609 17.8148ZM22.893 16.3344L20.8609 14.6596V11.2929C20.8609 11.1325 20.7296 11.0012 20.5692 11.0012H19.2079C19.0475 11.0012 18.9162 11.1325 18.9162 11.2929V13.0577L16.7407 11.2662C16.3104 10.9113 15.6881 10.9113 15.2579 11.2662L9.10551 16.3344C8.98154 16.4365 8.96453 16.6212 9.06662 16.7452L9.68648 17.4988C9.78857 17.6227 9.97331 17.6397 10.0973 17.5376L15.8145 12.8292C15.9215 12.7417 16.0771 12.7417 16.1864 12.8292L21.9037 17.5376C22.0277 17.6397 22.2124 17.6227 22.3145 17.4988L22.9344 16.7452C23.0365 16.6188 23.017 16.4365 22.893 16.3344Z' fill='white' />
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const FreeAndClearEquity: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute', top: '-07px', left: 0 }}>
    <Box
      css={isHighlighted ? `
        transform: scale(1.5) translate(0, -8px);
      ` : ''}
    >
      <svg width='34px' height='34px' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 127 121'>
        <path d='M44.302 74.896L10.345 71.32C4.783 40.92 34.62 10.49 65.083 10.315c26.783-.207 56.544 29.796 51.8 60.328l-32.51 4.28' fill='none' stroke='#1e9600' strokeWidth='14'/>
      </svg>
    </Box>
  </PinpointContainer>
)

const HighEquity: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute', top: '-20px', left: '-7px' }}>
    <Box
      css={isHighlighted ? `
        transform: scale(1.5) translate(-2px, -10px);
      ` : ''}
    >
      <svg width='21' height='24' viewBox='0 0 21 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M17.2787 6.27699C13.2288 5.51118 2.99455 9.85672 3.41773 20.0558L6.11761 20.5663' stroke='url(#paint0_linear)' strokeWidth='6' />
        <path d='M17.2622 0.150024L19.8603 7.78358H14.6641L17.2622 0.150024Z' fill='#625D52' />
        <defs>
          <linearGradient id='paint0_linear' x1='17.921' y1='7.16104' x2='2.29666' y2='18.5128' gradientUnits='userSpaceOnUse'>
            <stop stopColor='#38A1A8' />
            <stop offset='0.510417' stopColor='#F7D070' />
            <stop offset='1' stopColor='#FF0000' />
            <stop offset='1' stopColor='#D64545' />
          </linearGradient>
        </defs>
      </svg>
    </Box>
  </PinpointContainer>
)

const LowEquity: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute', top: '-20px', left: '-12px' }}>
    <Box
      css={isHighlighted ? `
        transform: scale(1.5) translate(-4px, -10px);
      ` : ''}
    >
      <svg width='11' height='18' viewBox='0 0 11 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M6.11748 5.99999C4.50003 7 3.41763 12 3.41763 13.7536L6.11751 14.2641' stroke='url(#paint0_linear)' strokeWidth='6' />
        <path d='M2.39591 1.80544L9.06481 6.3383L4.91498 9.46543L2.39591 1.80544Z' fill='#625D52' />
        <defs>
          <linearGradient id='paint0_linear' x1='17.9208' y1='0.858917' x2='2.29651' y2='12.2106' gradientUnits='userSpaceOnUse'>
            <stop stopColor='#1E9600' />
            <stop offset='0.510417' stopColor='#FFF200' />
            <stop offset='1' stopColor='#FF0000' />
            <stop offset='1' stopColor='#FF0000' />
          </linearGradient>
        </defs>
      </svg>
    </Box>
  </PinpointContainer>
)

const UpsideDown: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute', top: '-8px', left: '-10px' }}>
    <Box
      css={isHighlighted ? `
        transform: scale(1.5) translate(-4px, -5px);
      ` : ''}
    >
      <svg width='12' height='14' viewBox='0 0 12 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M10.6066 10.5272C8.71448 10.3375 5.54979 6.31801 4.78104 4.74183L6.98387 3.09942' stroke='#960000' strokeWidth='6' />
      </svg>
    </Box>
  </PinpointContainer>
)

const InStateAbsentee: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <circle stroke='#E9B949' fill='#FCEFC7' r='12.5' cy='16' cx='16'/>
        <path fill='#FCEFC7' d='M9.772 24.534l12.94-.074-6.534 11.316-6.406-11.243z'/>
        <path stroke='#E9B949' d='M10 27c5.294 3.52 5.765 9.827 6 11M22 27c-4.667 4.23-5.765 9.827-6 11'/>
        <path fill='#C99A2E' d='M17.745 18.855c0 .25-.203.452-.453.452h-2.716a.453.453 0 01-.453-.452v-1.358H8.691v4.074c0 .724.634 1.358 1.358 1.358h11.77c.724 0 1.358-.634 1.358-1.358v-4.074h-5.432zm4.074-5.885h-2.263V11.61c0-.724-.634-1.358-1.359-1.358H13.67c-.724 0-1.358.634-1.358 1.358v1.359H10.05c-.724 0-1.358.633-1.358 1.358v2.263h14.486v-2.263c0-.725-.634-1.358-1.358-1.358zm-4.074 0h-3.622v-.906h3.622z'/>
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const OutOfXAbsentee: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='16' cy='16' r='12.5' fill='#FCEFC7' stroke='#E9B949' />
        <path d='M9.77212 24.5335L22.7114 24.4598L16.1779 35.7761L9.77212 24.5335Z' fill='#FCEFC7' />
        <path d='M10 27C15.2941 30.52 15.7647 36.8267 16 38' stroke='#E9B949' />
        <path d='M22 27C17.3333 31.2308 16.2353 36.8267 16 38' stroke='#E9B949' />
        <path fill='#C99A2E' d='m22.404212,15.032607l-3.44459,0l-3.16762,-4.67816a0.48265,0.40729 0 0 0 -0.41893,-0.20499l-1.97411,0c-0.32038,0 -0.55154,0.25866 -0.46354,0.51858l1.47772,4.36457l-3.1001,0l-1.30201,-1.46495c-0.09102,-0.10249 -0.23418,-0.16277 -0.38578,-0.16277l-1.20526,0c-0.31375,0 -0.54401,0.24874 -0.46776,0.50561l0.94968,2.74982l-0.94968,2.74982c-0.07625,0.25688 0.15401,0.50561 0.46776,0.50561l1.20526,0c0.1519,0 0.29476,-0.06027 0.38578,-0.16277l1.30201,-1.46494l3.1001,0l-1.47772,4.36431c-0.088,0.25993 0.14316,0.51883 0.46354,0.51883l1.97411,0c0.173,0 0.33273,-0.07833 0.41863,-0.20499l3.16792,-4.67815l3.44459,0c1.06541,0 2.89335,-0.72866 2.89335,-1.62771s-1.82794,-1.62772 -2.89335,-1.62772z' />
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const OwnerOcc: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='16' cy='16' r='12.5' fill='#C9F5F8' stroke='#2B959C' />
        <path d='M9.77212 24.5335L22.7114 24.4598L16.1779 35.7761L9.77212 24.5335Z' fill='#C9F5F8' />
        <path d='M10 27C15.2941 30.52 15.7647 36.8267 16 38' stroke='#2B959C' />
        <path d='M22 27C17.3333 31.2308 16.2353 36.8267 16 38' stroke='#2B959C' />
        <path d='M16 16C17.8938 16 19.4286 14.4332 19.4286 12.5C19.4286 10.5668 17.8938 9 16 9C14.1063 9 12.5714 10.5668 12.5714 12.5C12.5714 14.4332 14.1063 16 16 16ZM18.4 16.875H17.9527C17.358 17.1539 16.6964 17.3125 16 17.3125C15.3036 17.3125 14.6446 17.1539 14.0473 16.875H13.6C11.6125 16.875 10 18.5211 10 20.55V21.6875C10 22.4121 10.5759 23 11.2857 23H20.7143C21.4241 23 22 22.4121 22 21.6875V20.55C22 18.5211 20.3875 16.875 18.4 16.875Z' fill='#1F878E' />
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const Trust: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='16' cy='16' r='12.5' fill='#D1EEFC' stroke='#3994C1' />
        <path d='M9.77212 24.5335L22.7114 24.4598L16.1779 35.7761L9.77212 24.5335Z' fill='#D1EEFC' />
        <path d='M10 27C15.2941 30.52 15.7647 36.8267 16 38' stroke='#3994C1' />
        <path d='M22 27C17.3333 31.2308 16.2353 36.8267 16 38' stroke='#3994C1' />
        <path d='M17.125 12.7188V9H11.6562C11.2926 9 11 9.29258 11 9.65625V22.3438C11 22.7074 11.2926 23 11.6562 23H20.8438C21.2074 23 21.5 22.7074 21.5 22.3438V13.375H17.7812C17.4203 13.375 17.125 13.0797 17.125 12.7188ZM18.875 19.1719C18.875 19.3523 18.7273 19.5 18.5469 19.5H13.9531C13.7727 19.5 13.625 19.3523 13.625 19.1719V18.9531C13.625 18.7727 13.7727 18.625 13.9531 18.625H18.5469C18.7273 18.625 18.875 18.7727 18.875 18.9531V19.1719ZM18.875 17.4219C18.875 17.6023 18.7273 17.75 18.5469 17.75H13.9531C13.7727 17.75 13.625 17.6023 13.625 17.4219V17.2031C13.625 17.0227 13.7727 16.875 13.9531 16.875H18.5469C18.7273 16.875 18.875 17.0227 18.875 17.2031V17.4219ZM18.875 15.4531V15.6719C18.875 15.8523 18.7273 16 18.5469 16H13.9531C13.7727 16 13.625 15.8523 13.625 15.6719V15.4531C13.625 15.2727 13.7727 15.125 13.9531 15.125H18.5469C18.7273 15.125 18.875 15.2727 18.875 15.4531ZM21.5 12.3332V12.5H18V9H18.1668C18.3418 9 18.5086 9.06836 18.6316 9.19141L21.3086 11.8711C21.4316 11.9941 21.5 12.1609 21.5 12.3332Z' fill='#2D83AE' />
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const BankOwned: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='16' cy='16' r='12.5' fill='#FACDCD' stroke='#BA2525' />
        <path d='M9.77212 24.5335L22.7114 24.4598L16.1779 35.7761L9.77212 24.5335Z' fill='#FACDCD' />
        <path d='M10 27C15.2941 30.52 15.7647 36.8267 16 38' stroke='#BA2525' />
        <path d='M22 27C17.3333 31.2308 16.2353 36.8267 16 38' stroke='#BA2525' />
        <path d='M23 11.8V12.2667C23 12.3286 22.9754 12.3879 22.9317 12.4317C22.8879 12.4754 22.8286 12.5 22.7667 12.5H22.0667V12.85C22.0667 13.0433 21.91 13.2 21.7167 13.2H10.2833C10.09 13.2 9.93333 13.0433 9.93333 12.85V12.5H9.23333C9.17145 12.5 9.1121 12.4754 9.06834 12.4317C9.02458 12.3879 9 12.3286 9 12.2667V11.8C9 11.7538 9.01369 11.7087 9.03935 11.6703C9.065 11.632 9.10146 11.6021 9.14411 11.5844L15.9108 9.01774C15.9679 8.99409 16.0321 8.99409 16.0892 9.01774L22.8559 11.5844C22.8985 11.6021 22.935 11.632 22.9607 11.6703C22.9863 11.7087 23 11.7538 23 11.8ZM22.3 20.6667H9.7C9.3134 20.6667 9 20.9801 9 21.3667V21.8333C9 21.8952 9.02458 21.9546 9.06834 21.9983C9.1121 22.0421 9.17145 22.0667 9.23333 22.0667H22.7667C22.8286 22.0667 22.8879 22.0421 22.9317 21.9983C22.9754 21.9546 23 21.8952 23 21.8333V21.3667C23 20.9801 22.6866 20.6667 22.3 20.6667ZM11.3333 13.6667V19.2667H10.2833C10.09 19.2667 9.93333 19.4234 9.93333 19.6167V20.2H22.0667V19.6167C22.0667 19.4234 21.91 19.2667 21.7167 19.2667H20.6667V13.6667H18.8V19.2667H16.9333V13.6667H15.0667V19.2667H13.2V13.6667H11.3333Z' fill='#A61B1B' />
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const Corporate: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' xmlns='http://www.w3.org/2000/svg'>
        <circle stroke='#fd7e14' fill='#ffd8a8' r='12.5' cy='16' cx='16'/>
        <path fill='#ffd8a8' d='m9.77212,24.5335l12.93928,-0.0737l-6.5335,11.3163l-6.40578,-11.2426z'/>
        <path fill='transparent' stroke='#fd7e14' d='m10,27c5.2941,3.52 5.7647,9.8267 6,11'/>
        <path fill='transparent' stroke='#fd7e14' d='m22,27c-4.6667,4.2308 -5.7647,9.8267 -6,11'/>
        <path stroke='null' d='m21.198865,22.109299l-0.502905,0l0,-11.466224c0,-0.3333 -0.270185,-0.603485 -0.603485,-0.603485l-8.448796,0c-0.3333,0 -0.603485,0.270185 -0.603485,0.603485l0,11.466224l-0.502905,0c-0.166637,0 -0.301743,0.135105 -0.301743,0.301743l0,0.502905l11.265062,0l0,-0.502905c0,-0.166637 -0.135105,-0.301743 -0.301743,-0.301743zm-7.74473,-10.158672c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,1.005809c0,0.166637 -0.135105,0.301743 -0.301743,0.301743l-1.005809,0c-0.166637,0 -0.301743,-0.135105 -0.301743,-0.301743l0,-1.005809zm0,2.413942c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,1.005809c0,0.166637 -0.135105,0.301743 -0.301743,0.301743l-1.005809,0c-0.166637,0 -0.301743,-0.135105 -0.301743,-0.301743l0,-1.005809zm1.307552,3.721494l-1.005809,0c-0.166637,0 -0.301743,-0.135105 -0.301743,-0.301743l0,-1.005809c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,1.005809c0,0.166637 -0.135105,0.301743 -0.301743,0.301743zm1.911037,4.023236l-1.609295,0l0,-2.112199c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,2.112199zm1.609295,-4.324979c0,0.166637 -0.135105,0.301743 -0.301743,0.301743l-1.005809,0c-0.166637,0 -0.301743,-0.135105 -0.301743,-0.301743l0,-1.005809c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,1.005809zm0,-2.413942c0,0.166637 -0.135105,0.301743 -0.301743,0.301743l-1.005809,0c-0.166637,0 -0.301743,-0.135105 -0.301743,-0.301743l0,-1.005809c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,1.005809zm0,-2.413942c0,0.166637 -0.135105,0.301743 -0.301743,0.301743l-1.005809,0c-0.166637,0 -0.301743,-0.135105 -0.301743,-0.301743l0,-1.005809c0,-0.166637 0.135105,-0.301743 0.301743,-0.301743l1.005809,0c0.166637,0 0.301743,0.135105 0.301743,0.301743l0,1.005809z' fill='#fd7e14'/>
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const Military: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32' height='39' viewBox='0 0 32 39' xmlns='http://www.w3.org/2000/svg'>
        <circle stroke='#656f2b' fill='#a5b646' r='12.5' cy='16.125' cx='16'/>
        <path fill='#a5b646' stroke='transparent' d='m9.83462,24.5335l12.93928,-0.0737l-6.5335,11.3163l-6.40578,-11.2426z'/>
        <path stroke='#656f2b' fill='transparent' d='m10,27c5.2941,3.52 5.7647,9.8267 6,11'/>
        <path stroke='#656f2b' fill='transparent' d='m22.125,27c-4.6667,4.2308 -5.7647,9.8267 -6,11'/>
        <path stroke='transparent' fill='#656f2b' d='m21.38637,12.75278l-5.11251,-2.13021a1.28212,1.28212 0 0 0 -0.98256,0l-5.11251,2.13021c-0.47663,0.19704 -0.78818,0.66303 -0.78818,1.1796c0,5.28559 3.04887,8.9389 5.89802,10.12649c0.31421,0.13048 0.66835,0.13048 0.98256,0c2.28199,-0.95061 5.90069,-4.2338 5.90069,-10.12649c0,-0.51658 -0.31154,-0.98256 -0.78552,-1.1796zm-5.60246,9.65518l-0.00266,-10.14513l4.6838,1.95181c-0.08787,4.03143 -2.18613,6.95248 -4.68114,8.19333z' />
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const Unknown: MarkerGraphic = ({ isHighlighted }) => (
  <PinpointContainer style={{ position: 'absolute' }}>
    <HighlightContainerMarker isHighlighted={isHighlighted}>
      <svg width='32px' height='39px' version='1.1' viewBox='0 0 32 39' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='16' cy='16' r='12.5' fill='#e8e6e1' stroke='#857f72'/>
        <path d='m9.7721 24.534 12.939-0.0737-6.5335 11.316-6.4058-11.243z' fill='#e8e6e1'/>
        <path d='m10 27c5.2941 3.52 5.7647 9.8267 6 11' fill='none' stroke='#857f72'/>
        <path d='m22 27c-4.6667 4.2308-5.7647 9.8267-6 11' fill='none' stroke='#857f72'/>
        <path d='m15.882 9.2454c-2.3945 0-3.9454 0.98105-5.163 2.7307-0.22088 0.31739-0.15278 0.75255 0.15534 0.98618l1.2941 0.98122c0.31118 0.23594 0.75393 0.18077 0.99755-0.12443 0.75144-0.94139 1.3088-1.4834 2.4826-1.4834 0.92288 0 2.0644 0.59395 2.0644 1.4889 0 0.67653-0.55849 1.024-1.4697 1.5349-1.0626 0.59577-2.4689 1.3372-2.4689 3.192v0.29363c0 0.39763 0.32234 0.71997 0.71997 0.71997h2.174c0.39763 0 0.71997-0.32234 0.71997-0.71997v-0.17318c0-1.2857 3.7579-1.3393 3.7579-4.8186 3.3e-5 -2.6202-2.7179-4.6078-5.2642-4.6078zm-0.30062 11.203c-1.1458 0-2.078 0.93221-2.078 2.078 0 1.1458 0.93221 2.078 2.078 2.078 1.1458 0 2.078-0.93221 2.078-2.078 0-1.1458-0.93221-2.078-2.078-2.078z' fill='#857f72' strokeWidth='.029999'/>
      </svg>
    </HighlightContainerMarker>
  </PinpointContainer>
)

const OWNER_TYPE_TO_MARKER: { [key in OwnerType]: MarkerGraphic } = {
  absentee: InStateAbsentee,
  outofstate: OutOfXAbsentee,
  outofcountry: OutOfXAbsentee,
  home: Home,
  owneroccupied: OwnerOcc,
  trustowned: Trust,
  bankowned: BankOwned,
  companyowned: Corporate,
  military: Military
}
